import { useForm } from '@tanstack/react-form';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import Alert from '~/common/components/Alerts/Alert';
import Box from '~/common/components/Box';
import Checkbox from '~/common/components/Checkboxes/Checkbox/Checkbox';
import GlobalLayout from '~/common/components/GlobalLayout';
import Stack from '~/common/components/Stacks/Stack';
import Text from '~/common/components/Texts/Text';
import { UnderwriterCheckboxProps } from '~/common/components/UnderwriterCheckbox/UnderwriterCheckbox';
import {
  DOCUMENT_TYPE_KEYS,
  LOAN_SUMMARY_STATUS_BY_NAME,
  RULE_ID,
  TRACK_TYPE_DESCRIPTIONS,
} from '~/common/constants';
import lang from '~/common/constants/language.json';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import { Chip } from '~/common/mui/Chip';
import { Accordion } from '~/common/mui/MUIAccordion';
import { formatCurrency } from '~/common/util/format';
import { useNavigation } from '~/stores/navigationStore';

import { HomeActions } from '../Home/redux';
import { PayeeCard } from './common/BreakdownComponents/PayeeCard';
import TaxSummaryItem from './common/BreakdownComponents/TaxSummaryItem';
import {
  TAX_PAYMENT_TYPES,
  WAIVE_ESCROW_OPTION_DISPLAY_NAMES,
} from './common/constants';
import { formatTaxDate } from './common/helpers';
import { useTaxBreakdownData } from './common/hooks/useTaxesData';
import language from './common/language.json';
import TaxProrations from './common/taxProrations.tsx';

export default function TaxBreakdown() {
  const { isLoading, isError, data, saveTaxBreakdown } = useTaxBreakdownData();
  const {
    data: loanData,
    isLoading: isLoanSummaryLoading,
    isError: loanSummaryError,
  } = useGetLoanSummary();

  const navigationItems = useNavigation();
  const selectedAlert = navigationItems.selectedAlert;
  const isNoDocsPage =
    selectedAlert.ruleId === RULE_ID.TAX_BREAKDOWN_NO_DOCS_RULE_ID;

  const dispatch = useDispatch();
  const [isProrationsExpanded, setIsProrationsExpanded] = useState(false);
  const formMethods = useForm({
    defaultValues: {
      isVerified: false,
      isFinalized: false,
      prorationsData: [],
    },
  });

  const isRefi = loanData?.propertyData.isRefi;

  const flattenNavigationAlerts = (alerts = []) => {
    const flattenedAlerts = [];

    alerts.forEach((alert) => {
      flattenedAlerts.push(alert);

      const children = alert?.gutterItemChildren ?? alert?.children ?? [];

      if (children.length > 0) {
        flattenedAlerts.push(...children);
      }
    });

    return flattenedAlerts;
  };

  const escrowGuttersCompleted = useMemo(() => {
    const gutterItems = flattenNavigationAlerts(
      (navigationItems || { allAlerts: [] }).allAlerts,
    );

    const escrowDetailItems = gutterItems.filter(
      (item) => item.ruleId === RULE_ID.TAX_ESCROW_DETAILS_RULE_ID,
    );

    if (escrowDetailItems.length === 0) {
      return true;
    }

    return escrowDetailItems.every(
      (item) => item.status === 'cleared' || item.status === 'verified',
    );
  }, [navigationItems]);

  const mapProration = (p) => {
    const getYear = (dateStr) => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      return Number.isNaN(date.getTime()) ? null : date.getFullYear();
    };
    const fromYear = getYear(p.fromDate);
    const toYear = getYear(p.toDate);
    const hasAmount = !!Number(p.amount);
    return {
      companyName: p.companyName || '',
      amount: p.amount ?? 0,
      sellerPaidAmount: p.sellerPaidAmount ?? 0,
      toDate: toYear === 1800 ? '' : p.toDate,
      fromDate: fromYear === 1800 ? '' : p.fromDate,
      taxesPaidFlag: hasAmount ? p.taxesPaidFlag || null : null,
      originalHudNumber: p.originalHudNumber,
    };
  };

  const allTaxDocuments = useMemo(
    () => [
      ...(data?.taxDocuments ?? []),
      ...(data?.escrowDetails ?? []).flatMap(
        (detail) => detail.taxDocuments ?? [],
      ),
    ],
    [data?.taxDocuments, data?.escrowDetails],
  );

  const { showEarlyCdVerified, showCdFinalized, hasNonEstimateDocs } =
    useMemo(() => {
      if (data == null || loanData == null) {
        return {
          showEarlyCdVerified: false,
          showCdFinalized: false,
          hasNonEstimateDocs: false,
        };
      }

      const isTracLite =
        loanData.loanInfo.tracTypeDescription === TRACK_TYPE_DESCRIPTIONS.Doma;
      const isTracPlus =
        loanData.loanInfo.tracTypeDescription ===
        TRACK_TYPE_DESCRIPTIONS.TracPlus;
      const isUnderwriting =
        loanData?.loanInfo?.loanStatusId ===
        LOAN_SUMMARY_STATUS_BY_NAME.UNDERWRITING;
      const hasNonEstimateDocs = allTaxDocuments.some(
        (d) =>
          d.documentTypeKey === DOCUMENT_TYPE_KEYS.TAX_INFORMATION ||
          d.documentTypeKey ===
            DOCUMENT_TYPE_KEYS.PRELIMINARY_TITLE_COMMITMENT ||
          (isTracLite &&
            d.documentTypeKey ===
              DOCUMENT_TYPE_KEYS.PRE_CLOSING_STATUS_REPORT) ||
          (isTracPlus &&
            d.documentTypeKey === DOCUMENT_TYPE_KEYS.TITLE_SUMMARY),
      );
      const hasCoreLogicTaxEstimate = allTaxDocuments.some(
        (d) =>
          d.documentTypeKey === DOCUMENT_TYPE_KEYS.TAX_INFORMATION_CORE_LOGIC,
      );
      return {
        showEarlyCdVerified:
          !isNoDocsPage &&
          isUnderwriting &&
          !hasNonEstimateDocs &&
          hasCoreLogicTaxEstimate,
        showCdFinalized: !isNoDocsPage && hasNonEstimateDocs,
        hasNonEstimateDocs,
      };
    }, [allTaxDocuments, data, loanData, isNoDocsPage]);

  useEffect(() => {
    if (data) {
      formMethods.setFieldValue(
        'isVerified',
        data.isTaxVerifiedForEarlyCD ?? false,
      );
      formMethods.setFieldValue('isFinalized', data.isTaxVerified ?? false);
      if (data.taxProrations) {
        formMethods.setFieldValue(
          'prorationsData',
          data.taxProrations.map(mapProration),
        );
      }
    }
  }, [data, formMethods]);

  const isUnderwritingStatus =
    loanData?.loanInfo?.loanStatusId ===
    LOAN_SUMMARY_STATUS_BY_NAME.UNDERWRITING;
  const isApprovedWithConditionsStatus =
    loanData?.loanInfo?.loanStatusId ===
    LOAN_SUMMARY_STATUS_BY_NAME.APPROVED_WITH_CONDITIONS;

  const hasMissingPayeeItems = useMemo(() => {
    const gutterItems = flattenNavigationAlerts(
      (navigationItems || { allAlerts: [] }).allAlerts,
    );

    return gutterItems.some(
      (item) => item.ruleId === RULE_ID.MISSING_PAYEE_RULE_ID,
    );
  }, [navigationItems]);

  const missingPayeeTaxIds = useMemo(() => {
    const gutterItems = flattenNavigationAlerts(
      (navigationItems || { allAlerts: [] }).allAlerts,
    );

    return new Set(
      gutterItems
        .filter((item) => item.ruleId === RULE_ID.MISSING_PAYEE_RULE_ID)
        .map(
          (item) =>
            item.additionalReferences?.TaxId ??
            item.additionalReferences?.taxId ??
            null,
        )
        .map((taxId) => (typeof taxId === 'string' ? taxId.trim() : taxId))
        .filter((taxId): taxId is string => Boolean(taxId)),
    );
  }, [navigationItems]);

  const visibleEscrowDetails = useMemo(
    () =>
      (data?.escrowDetails ?? []).filter((detail) => {
        const taxId = (detail.taxId ?? '').trim();
        const payeeCode = (detail.payeeCode ?? '').trim();
        const payeeName = (detail.payeeName ?? '').trim();
        const isMissingPayeeByFields = !payeeCode && !payeeName;

        return !missingPayeeTaxIds.has(taxId) && !isMissingPayeeByFields;
      }),
    [data?.escrowDetails, missingPayeeTaxIds],
  );

  const hasTaxDocuments = allTaxDocuments.length > 0 || !!data?.hasCoreLogicDoc;
  const shouldShowPayeeCodeVerificationWarning =
    hasMissingPayeeItems && hasTaxDocuments;
  const shouldUseReadOnlyTaxVerificationCheckbox =
    shouldShowPayeeCodeVerificationWarning &&
    (isUnderwritingStatus || isApprovedWithConditionsStatus);
  const shouldDisableUnderwriterCheckboxForMissingPayee =
    shouldShowPayeeCodeVerificationWarning && isApprovedWithConditionsStatus;

  useEffect(() => {
    const shouldDisableUnderwriterCheckbox =
      !escrowGuttersCompleted ||
      data?.isCalculationMethodNotProvided ||
      shouldDisableUnderwriterCheckboxForMissingPayee;
    dispatch(
      HomeActions.setIsUnderwriterCheckboxDisabled(
        shouldDisableUnderwriterCheckbox,
      ),
    );
  }, [
    data,
    dispatch,
    escrowGuttersCompleted,
    shouldDisableUnderwriterCheckboxForMissingPayee,
  ]);

  const fifteenPercentPadding = 1.15;

  const paddedTotal = (data?.escrowDetails ?? [])
    .flatMap((detail) => [
      ...(detail.additionalTaxes ?? []),
      ...(detail.escrowInstallments ?? []),
    ])
    .filter(
      (tax) => tax.paidType === TAX_PAYMENT_TYPES.PAST_BILL_PAY_AT_CLOSING,
    )
    .reduce(
      (sum, tax) => sum + tax.installmentAmount * fifteenPercentPadding,
      0,
    );
  const supplementalDueAtClosingTotal = (data?.escrowDetails ?? []).reduce(
    (sum, detail) => sum + (detail.supplementalDueAtClosing ?? 0),
    0,
  );
  const supplementalInstallmentsDueAtClosingTotal = (
    data?.escrowDetails ?? []
  ).reduce(
    (sum, detail) =>
      sum +
      (detail.supplementalInstallments ?? [])
        .filter(
          (installment) =>
            installment.paidType ===
            TAX_PAYMENT_TYPES.BORROWER_TO_PAY_AT_CLOSING,
        )
        .reduce(
          (supplementalSum, installment) =>
            supplementalSum + (installment.supplementalAmount ?? 0),
          0,
        ),
    0,
  );
  const nonAdValorumTotal = (data?.escrowDetails ?? []).reduce(
    (sum, detail) => sum + (detail.nonAdValorum ?? 0),
    0,
  );

  const taxesPaidAtClosingTotal = isRefi
    ? (data?.nonDelinquentTaxesPaidAtClosing ?? 0) +
      paddedTotal +
      supplementalDueAtClosingTotal +
      supplementalInstallmentsDueAtClosingTotal
    : (data?.taxesPaidAtClosing ?? 0) +
      paddedTotal +
      supplementalDueAtClosingTotal +
      supplementalInstallmentsDueAtClosingTotal;

  const borrowerToPayAtClosing = isRefi
    ? taxesPaidAtClosingTotal + data?.delinquentTaxesPaidAtClosing
    : taxesPaidAtClosingTotal;

  const underwriterCheckbox = useMemo(() => {
    if (
      !hasNonEstimateDocs &&
      loanData?.loanInfo?.loanStatusId ===
        LOAN_SUMMARY_STATUS_BY_NAME.APPROVED_WITH_CONDITIONS
    ) {
      return {
        isHidden: true,
      } as UnderwriterCheckboxProps;
    } else if (isNoDocsPage) {
      return {
        heading: null,
        text: 'No Documents Provided',
      } as UnderwriterCheckboxProps;
    } else return undefined;
  }, [loanData?.loanInfo?.loanStatusId, isNoDocsPage, hasNonEstimateDocs]);

  const handleSubmit = async () => {
    try {
      await formMethods.handleSubmit();
      const isFormValid = formMethods.state.isValid;
      if (!isFormValid) {
        setIsProrationsExpanded(true);
        return false;
      }
      const values = formMethods.state.values;
      const saveData = {
        propertyTaxData: {
          ...data,
          taxProrations: values.prorationsData?.map((p, index) => ({
            ...data.taxProrations[index],
            ...p,
            fromDate: p.fromDate || null,
            toDate: p.toDate || null,
          })),
          isTaxVerifiedForEarlyCD: values.isVerified,
          isTaxVerified: values.isFinalized,
        },
        fieldUpdateStatus: true,
      };

      await saveTaxBreakdown(saveData);
    } catch {
      return false;
    }
  };
  return (
    <GlobalLayout
      hasNewBackground
      isLoading={isLoading || isLoanSummaryLoading}
      heading="Tax Breakdown"
      primaryButton={{ onSubmit: handleSubmit }}
      error={(isError || loanSummaryError) && lang.DEFAULT_OOPS_TITLE}
      contentProps={{
        sx: {
          maxWidth: '70.25rem',
          margin: '0 auto',
        },
        underwriterCheckbox,
      }}
    >
      <Stack
        sx={{
          gap: 4,
        }}
      >
        <Box
          sx={{
            alignSelf: 'flex-end',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            paddingRight: '0.625rem',
          }}
        >
          <h3 style={{ color: '#2A69AC' }}>
            Escrow/Impound Waiver Type:{' '}
            {WAIVE_ESCROW_OPTION_DISPLAY_NAMES[data?.escrowWaiverOption] ??
              WAIVE_ESCROW_OPTION_DISPLAY_NAMES[1]}
          </h3>
        </Box>
        <Accordion
          defaultExpanded
          header={
            <Stack
              sx={{
                gap: 2,
                direction: 'row',
                justifyContent: 'space-between',
                flex: 1,
                paddingRight: 8,
              }}
            >
              <Text variant="h6">Summary</Text>
            </Stack>
          }
        >
          <Stack
            sx={{
              width: '100%',
              gap: 3,
              p: '0 2rem 0 2rem',
            }}
          >
            <TaxSummaryItem
              label={
                loanData?.loanSummaryDates?.actualClosingDate === null
                  ? 'Estimated Closing Date'
                  : 'Closing Date'
              }
              content={formatTaxDate(
                loanData?.loanSummaryDates?.actualClosingDate?.toDate() ??
                  loanData?.loanSummaryDates?.estimatedClosingDate?.toDate(),
              )}
            />
            <TaxSummaryItem
              label="First Payment Date"
              content={formatTaxDate(
                loanData?.loanSummaryDates?.firstPaymentDate?.toDate(),
              )}
            />
            <TaxSummaryItem
              label="Initial Escrow Payment at Closing (Section G)"
              content={formatCurrency(
                (data?.annualTaxAmount ?? 0) + nonAdValorumTotal,
              )}
              color="success"
              data-testid="escrow-collected-at-closing"
            />
            {!isRefi && (
              <TaxSummaryItem
                label="Taxes Paid at Closing (Section K)"
                content={formatCurrency(taxesPaidAtClosingTotal)}
                color="success"
                data-testid="taxes-paid-at-closing-total"
              />
            )}
            {!isRefi && (
              <TaxSummaryItem
                label="Seller to Pay at Closing"
                content={formatCurrency(data?.sellerToPayAtClosing)}
                color="success"
              />
            )}
            {isRefi && (
              <TaxSummaryItem
                label="Delinquent Taxes Paid at Closing (Section K)"
                content={formatCurrency(data?.delinquentTaxesPaidAtClosing)}
                color="success"
              />
            )}
            {isRefi && (
              <TaxSummaryItem
                label="Non-Delinquent Taxes Paid at Closing (Section K)"
                content={formatCurrency(taxesPaidAtClosingTotal)}
                color="success"
                data-testid="taxes-paid-at-closing-total"
              />
            )}
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                gap: 4,
              }}
            >
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <Text
                  sx={{
                    fontSize: '1rem',
                    fontWeight: '600',
                  }}
                >
                  Borrower to Pay at Closing
                </Text>
                <Chip
                  label={`= ${formatCurrency(borrowerToPayAtClosing)}`}
                  sx={{
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                  }}
                  color="success"
                  data-testid="borrower-to-pay-chip"
                />
              </Stack>
              {!isRefi && (
                <Stack
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <Text
                    sx={{
                      fontSize: '1rem',
                      fontWeight: '500',
                    }}
                  >
                    Seller to Pay at Closing
                  </Text>
                  <Chip
                    label={`= ${formatCurrency(data?.sellerToPayAtClosing)}`}
                    sx={{
                      borderRadius: '0.5rem',
                      fontWeight: '600',
                    }}
                    color="success"
                    data-testid="seller-to-pay-chip"
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        </Accordion>
        <Stack
          sx={{
            gap: 4,
          }}
        >
          {visibleEscrowDetails.map(
            ({
              isBorrowerExempt,
              escrowInstallments,
              additionalTaxes,
              supplementalInstallments,
              payeeName,
              nonAdValorum,
              ...escrowDetail
            }) => (
              <PayeeCard
                key={payeeName}
                payeeName={payeeName}
                isExempt={isBorrowerExempt}
                installments={escrowInstallments}
                additionalTaxes={additionalTaxes}
                supplementalTaxes={(supplementalInstallments ?? []).map(
                  (installment) => ({
                    taxId: installment.taxId ?? '',
                    distributionDate: installment.supplementalDate,
                    installmentAmount: installment.supplementalAmount,
                    paidType: installment.paidType,
                  }),
                )}
                supplementalDueAtClosing={escrowDetail.supplementalDueAtClosing}
                nonAdValorum={nonAdValorum}
                loanData={loanData}
                isRefinance={isRefi}
                escrowWaiverOption={data?.escrowWaiverOption}
              />
            ),
          )}
        </Stack>
        {!isRefi && (
          <Accordion
            expanded={isProrationsExpanded}
            onChange={(_event, expanded) => setIsProrationsExpanded(expanded)}
            header={
              <Stack
                sx={{
                  gap: 2,
                  direction: 'row',
                  justifyContent: 'space-between',
                  flex: 1,
                  paddingRight: 8,
                }}
              >
                <Text variant="h6"> {language.TAX_PRORATIONS}</Text>
              </Stack>
            }
            sx={{ mb: '1rem' }}
          >
            <TaxProrations form={formMethods} />
          </Accordion>
        )}
        {shouldShowPayeeCodeVerificationWarning && (
          <Alert
            status="warning"
            title=" "
            data-testid="missingPayeeVerificationAlert"
          >
            {language.HUD_LINE_ALERT}
          </Alert>
        )}
        {showEarlyCdVerified && (
          <Box
            sx={{
              borderWidth: '0.0625rem',
              borderColor: 'blue.500',
              borderRadius: '0.25rem',
              bg: 'blue.50',
              width: '100%',
              p: 4,
              my: 2,
            }}
            data-testid="underwriter-checkbox"
          >
            <formMethods.Field name="isVerified">
              {(field) => (
                <Checkbox
                  name={field.name}
                  fontSize=".75rem"
                  fontWeight="400"
                  isChecked={!!field.state.value}
                  isDisabled={
                    data.isTaxVerifiedForEarlyCdPreviously ||
                    shouldUseReadOnlyTaxVerificationCheckbox
                  }
                  onChange={(e) => field.handleChange(e.target.checked)}
                >
                  {language.TAXES_VERIFIED_EARLY_CD}
                </Checkbox>
              )}
            </formMethods.Field>
          </Box>
        )}
        {showCdFinalized && (
          <Box
            sx={{
              borderWidth: '0.0625rem',
              borderColor: 'blue.500',
              borderRadius: '0.25rem',
              bg: 'blue.50',
              width: '100%',
              p: 4,
              my: 2,
            }}
            data-testid="underwriter-checkbox"
          >
            <formMethods.Field name="isFinalized">
              {(field) => (
                <Checkbox
                  name={field.name}
                  fontSize=".75rem"
                  fontWeight="400"
                  isChecked={!!field.state.value}
                  isDisabled={
                    data.isTaxVerifiedPreviously ||
                    shouldUseReadOnlyTaxVerificationCheckbox
                  }
                  onChange={(e) => field.handleChange(e.target.checked)}
                >
                  {language.TAXES_FINALIZED_CD}
                </Checkbox>
              )}
            </formMethods.Field>
          </Box>
        )}
      </Stack>
    </GlobalLayout>
  );
}
